import {
    Component,
    ViewEncapsulation,
    Input,
    forwardRef,
    ViewChild,
    ElementRef,
    EventEmitter,
    Output,
    HostListener
} from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BaseControlValueAccessor } from '../../models/forms/base-control-value-accessor';
import { UtilitiesService } from "../../services/utilities.service";
import { DropdownOption } from "../dropdown/dropdown-option";
import {throwError} from "rxjs";
import {NgSelectComponent} from "@ng-select/ng-select";
import {NgOption} from "@ng-select/ng-select/lib/ng-select.types";

@Component({
    selector: 'searchable-dropdown',
    templateUrl: './searchable-dropdown.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchableDropdownComponent),
            multi: true
        }
    ]
})

// Documentatino for underlying element
// https://ng-select.github.io/ng-select#/grouping
// https://github.com/ng-select/ng-select

export class SearchableDropdownComponent extends BaseControlValueAccessor<any> {

    @Input() clearValue: any = undefined;
    @Input() placeholderValue: string = null;
    @Input() placeholder: string = 'Select...';
    @Input() clearable: boolean = true;
    @Input() options: DropdownOption[];
    @Input() cssClasses: string = null;
    @Input() loading: boolean = false;
    @Input() small: boolean = true;

    // ngSelect pass-through
    @Input() virtualScroll = false;

    // OptGroup
    @Input() optGroupValueEmptyLabel: string = 'Group not set';
    @Input() optGroupTranslationMissingLabel: string = 'Group translation missing';
    @Input() optGroupTranslationLookup: {} | null = null;
    @Input() optGroupEnabled : boolean = false;
    @Input() optGroupSort: boolean = true;
    @Input() selectableGroup : boolean = false;
    @Input() selectableGroupExpand : boolean = true;

    // Able to append the dropdown element to the parent
    // to fix overflow issues
    @Input() appendTo : string = "";

    // Multiple options
    @Input() multiple: boolean = false;
    @Input() multipleShown: number = 2;

    @Output() focus: EventEmitter<any> = new EventEmitter<void>();

    @ViewChild('searchableDropdown', {static: true}) ngSelectComponent: NgSelectComponent;

    constructor(private element: ElementRef, protected controlContainer: ControlContainer, protected utilitiesService: UtilitiesService) {
        super(controlContainer, utilitiesService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.fixOptGroups();
    }

    fixOptGroups(): void {
        // ngSelect renders items with a null groupValue without using the optGroup format
        // and this looks terrible when mixing with optGroup format.
        // So we run through the data and make sure we fix all these instances
        if (this.optGroupEnabled && this.options && Array.isArray(this.options)) {
            this.options.forEach(item => {
                item.groupValue = item.groupValue === null ? "" : item.groupValue
            });

            // The ngSelect dropdown grouping will be based on whichever groupValue it comes across first.
            // This means that ultimately the options list will determine the optGroups ordering.
            // This is not what we are after, so we have to re-sort the options list
            // to make it render alphabetically (by opt-groups labels/names).
            if (this.optGroupSort) {
                const that = this;

                // Now sort the options by referring to this groups ordering
                this.options.sort(function (a, b) {
                    let groupA = '';
                    let groupB = '';

                    if (a.groupValue) {
                        if (that.optGroupTranslationLookup) {
                            groupA = that.optGroupTranslationLookup[a.groupValue] || that.optGroupTranslationMissingLabel;
                        } else groupA = a.groupValue;
                    } else groupA = that.optGroupValueEmptyLabel;

                    if (b.groupValue) {
                        if (that.optGroupTranslationLookup) {
                            groupB = that.optGroupTranslationLookup[b.groupValue] || that.optGroupTranslationMissingLabel;
                        } else groupB = b.groupValue;
                    } else groupB = that.optGroupValueEmptyLabel;

                    return groupA.localeCompare(groupB);
                });
            }
        }
    }

    // When a clear is triggered (user clicked the X button) it's simple we trigger undefined
    internalOnClear() : void {
        this.onChange(this.clearValue);
    }

    // Handling 4 different types as ngSelect will pass different types
    internalOnChange(param: [] | DropdownOption[] | DropdownOption | undefined) : void {

        if (param instanceof DropdownOption) {
            if (this.multiple == true) {
                // ngSelect always returns an array when you have enabled the multiple selector so we haev this guardning block here
                throwError("ngSelect returned a DropdownOption (item) as selected param but multiple option is true so should always be a list");
            }

            let val = param?.value;
            if (val === 'null') {
                val = null;
            }

            this.onChange(val);

        } else if (param instanceof Array) {

            if (this.multiple == false) {
                // ngSelect always returns an element when you have disabled the multiple selector so we have this guarding block here
                throwError("ngSelect returned a DropdownOption[] (list) as selected params but multiple option is false so should never be a list");
            }

            if (param.length == 0) {
                this.onChange(param)
            }
            else
            {

                // VANN - Can someone please get this working?
                // const latestExclusiveOption = param.reverse().find(option => option.exclusive);
                // if (latestExclusiveOption) {
                //     // If a latest exclusive option is selected, clear all other options
                //     param = [latestExclusiveOption];
                // } else {
                //     // Remove any exclusive option if a non-exclusive option is selected
                //     const nonExclusiveOptions = param.filter(option => !option.exclusive);
                //     if (nonExclusiveOptions.length !== param.length) {
                //         param = nonExclusiveOptions;
                //     }
                // }

                // When allowing multi-select you can get back a list of only DropdownOptions or a mixed list containing DropDownOptions and items looking like this { groupValue: X }.
                // To be able to pass this upwards we need to convert all groups (if selected) to their complete list of options. We can assume none of the options are selected within
                // any group we find because that is how ngSelect returns the array
                const selectedGroups = param.filter(function(item: any) { return !( item instanceof DropdownOption)} ).map(item => item.groupValue) as string[];
                const selectedOptions = param.filter(function(item: any) { return item instanceof DropdownOption} ) as DropdownOption[];

                if (selectedGroups.length == 0) {
                    // When multi=true we always return the DropdownOption[] list because the behaviour must be consistent with mixing in selectedGroupOptions
                    this.onChange(param);
                }
                else
                {
                    // The user has selected option groups, two ways we can deal with this. Either return the option group as a selection
                    // or expand it out to the options within that group
                    if (this.selectableGroupExpand) {
                        const selectedGroupOptions: DropdownOption[] = this.options.filter(item => {
                            return selectedGroups.includes(item.groupValue)
                        });

                        const mergedOptions = [...selectedGroupOptions, ...selectedOptions];

                        this.onChange(mergedOptions);
                    } else {
                        // We don't expand the option group, but we do have to convert them to DropdownOptions to return a consistent array type
                        const asOptions = selectedGroups.map(value => new DropdownOption("option-group", value));

                        const mergedOptions = [...asOptions, ...selectedOptions];

                        this.onChange(mergedOptions);
                    }
                }
            }
        } else if (param === undefined)
        {
            if (this.multiple == true) this.onChange([]);
            else this.onChange(undefined);
        }
    }

    writeValue(value: any): void {
        if (this.placeholder && (value === undefined || value === null || value === '')) {
            value = this.placeholderValue;
        }

        //When you set a value from a parent like form.get('zxw').setValue, set the DropdownOption value and not just its value,
        // it order to keep everything clean. Though, for the item to appear to be selected, you then need to set value as the actual value
        // and not the whole component. hence the if case.
        if (Array.isArray(value) && value.every(item => item instanceof DropdownOption)) {
            const extractedValues = value.map(item => item.value);
            super.writeValue(extractedValues);
        } if (value instanceof DropdownOption) {
            const extractedValue = value.value;
            super.writeValue(extractedValue);
        } else {
            super.writeValue(value);
        }
    }

    //Following the above, we also need this to bind correctly the values
    compareFn(item, selected) {
        if (selected instanceof DropdownOption) {
            return item.value === selected.value;
        } else {
            return item.value === selected;
        }
    }

    // ngSelect pass through methods
    items(value: any[] | null) {
        return (this.ngSelectComponent as any).items = value;
    }

    selectedItems(): NgOption[] {
        return (this.ngSelectComponent as any).selectedItems;
    }

    allItems(): NgOption[] {
        return (this.ngSelectComponent as any).itemsList.items;
    }

    unselect(item: NgOption) {
        (this.ngSelectComponent as any).unselect(item);
    }

    select(item: NgOption) {
        (this.ngSelectComponent as any).select(item);
    }

    selectedItemsByGroup(desiredGroupValue : any): NgOption[] {
        return this.selectedItems().filter(option => option.value.groupValue == desiredGroupValue);
    }

    allItemsByGroup(desiredGroupValue : any): NgOption[] {
        return (this.ngSelectComponent as any).itemsList.items.filter(option => option.value.groupValue == desiredGroupValue);
    }

    // NOTE: This method should only call the unselect/select methods *if* it
    // changes anything in the selection because otherwise it will cause a perpetual loop
    // with triggers.
    selectSingletonForGroup(desiredGroupValue: any, desiredValue: any | null) {
        const desiredItem = desiredValue === null ? null : this.allItemsByGroup(desiredGroupValue).find(option => option.value.value == desiredValue);
        const undesiredItems = this.selectedItemsByGroup(desiredGroupValue).filter(option => option.value.value != desiredValue);

        undesiredItems.forEach(item => this.unselect(item));
        if (desiredItem) this.select(desiredItem);
    }

    clearSingletonForGroup(desiredGroupValue: any) {
        this.selectSingletonForGroup(desiredGroupValue, null);
    }

    toggleItem(item: NgOption) {
        (this.ngSelectComponent as any).toggleItem(item);
    }

    get ngSelect() : NgSelectComponent {
        return this.ngSelectComponent;
    }

    public clearSelection() {
        this.ngSelectComponent.clearModel();
    }

    // Close dropdown when clicking outside of it
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: any): void {
        if (!this.ngSelectComponent.element.contains(event.target)) {
            this.ngSelectComponent.close();
        }
    }

    // Call this method to update the visual component state
    updateNgSelectComponentState(currentSelection: DropdownOption[]) {
        this.ngSelectComponent.clearModel();
        currentSelection.forEach(option => {
            this.ngSelectComponent.select(option);
        });
    }

    setDisabledState(isDisabled: boolean) {
        this.ngSelectComponent.setDisabledState(isDisabled);
    }

    searchFn = (term: string, item: { label: string; groupValue: any }) => {
        if (!term) return true;
        const t = term.toLowerCase();
        const groupLabel = this.optGroupTranslationLookup?.[item.groupValue] ?? String(item.groupValue ?? '');
        return (
            (item.label ?? '').toLowerCase().includes(t) ||
            groupLabel.toLowerCase().includes(t)
        );
    };
}
